import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import {
  User, Monitor, Bell, Settings as SettingsIcon, Info, Download, Trash2,
  KeyRound, ShieldCheck, LogOut, Sun, Moon, Check, Mail, MonitorSmartphone,
  Smartphone, Megaphone, Languages,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useUserHook } from '../../hooks/UserHook';
import { useAppContext } from '../../contexts/AppContext';
import { oauthClient } from '../../apis/OAuthClient';
import { useBootstrapTheme, ThemeMode } from '../../contexts/BootstrapThemeContext';
import { SUPPORTED_LANGUAGES } from '../../i18n';
import { FlagIcon } from '../../components/ui/FlagIcon';

interface Tab {
  id: string;
  name: string;
  icon: LucideIcon;
}

const TABS: Tab[] = [
  { id: 'profile',       name: 'Profile',       icon: User },
  { id: 'appearance',    name: 'Appearance',    icon: Monitor },
  { id: 'language',      name: 'Language',      icon: Languages },
  { id: 'notifications', name: 'Notifications', icon: Bell },
  { id: 'account',       name: 'Account',       icon: SettingsIcon },
];

const THEME_OPTIONS: { id: ThemeMode; label: string; icon: LucideIcon }[] = [
  { id: 'light', label: 'Light', icon: Sun },
  { id: 'dark',  label: 'Dark',  icon: Moon },
  { id: 'auto',  label: 'Auto',  icon: Monitor },
];

const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// Small preview swatch used in the Appearance tab - a rounded rectangle mimicking a topbar +
// content area in the given theme, so "Light/Dark/Auto" is something you recognize at a
// glance instead of just reading a word.
const ThemeSwatch = ({ variant }: { variant: 'light' | 'dark' | 'auto' }) => {
  if (variant === 'auto') {
    return (
      <div className="settings-swatch settings-swatch-auto">
        <div className="settings-swatch-half settings-swatch-half-light">
          <span className="settings-swatch-bar" />
        </div>
        <div className="settings-swatch-half settings-swatch-half-dark">
          <span className="settings-swatch-bar" />
        </div>
      </div>
    );
  }
  return (
    <div className={`settings-swatch settings-swatch-${variant}`}>
      <span className="settings-swatch-bar" />
    </div>
  );
};

export function BootstrapSettings() {
  const { t, i18n } = useTranslation(['settings', 'common']);
  const [user] = useUserHook();
  const { theme, effectiveTheme, setTheme } = useBootstrapTheme();
  const context = useAppContext();
  const navigate = useNavigate();
  // Deep-Link: /settings?tab=language springt direkt in den passenden Tab (z. B. aus dem
  // Sprach-Link im Topbar-Profilmenue). Unbekannter/fehlender Wert -> Profil-Tab.
  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(
    TABS.some(tab => tab.id === requestedTab) ? (requestedTab as string) : 'profile'
  );
  const [notifications, setNotifications] = useState({
    email: true,
    desktop: true,
    mobile: false,
    marketing: false,
  });

  const displayName = user?.user.displayName || user?.user.userName || 'Account';
  const userName = user?.user.userName;
  const showUserName = !!userName && userName !== displayName;

  const toggleNotification = (field: keyof typeof notifications) => {
    setNotifications(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleLogout = () => {
    context.setShowSidebar(false);
    context.setShowTopbar(false);
    oauthClient.logout();
    navigate('/login');
  };

  const handleExportSettings = () => {
    const data = {
      timestamp: new Date().toISOString(),
      version: '1.0.0',
      settings: localStorage.getItem('claudeflow_provider_config'),
      usage: localStorage.getItem('claudeflow_usage_metrics'),
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    // eslint-disable-next-line no-restricted-syntax -- JSON-Settings-Export, kein Media-Download, Art. 50 KI-VO nicht einschlaegig
    a.download = `sengilabs-settings-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleClearLocalData = () => {
    if (window.confirm(t('account.clearConfirm'))) {
      localStorage.clear();
      window.location.reload();
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return (
          <div className="settings-section">
            <div className="settings-section-title">{t('profile.title')}</div>
            <p className="settings-section-desc">{t('profile.description')}</p>

            <div className="settings-card">
              <div className="d-flex align-items-center gap-3 mb-4">
                <div className="settings-avatar settings-avatar-lg">{getInitials(displayName)}</div>
                <div>
                  <div className="fw-bold fs-5">{displayName}</div>
                  {showUserName && <div className="text-secondary small">{userName}</div>}
                </div>
              </div>

              <dl className="settings-deflist mb-0">
                <div className="settings-deflist-row">
                  <dt>{t('profile.username')}</dt>
                  <dd>{userName || '—'}</dd>
                </div>
                <div className="settings-deflist-row">
                  <dt>{t('profile.accountId')}</dt>
                  <dd className="sengi-num">{user?.user.id ?? '—'}</dd>
                </div>
              </dl>
            </div>

            <p className="settings-hint mt-3">
              <Info size={14} strokeWidth={2} className="flex-shrink-0" />
              {t('profile.hint')}
            </p>
          </div>
        );

      case 'appearance':
        return (
          <div className="settings-section">
            <div className="settings-section-title">{t('appearance.title')}</div>
            <p className="settings-section-desc">{t('appearance.description')}</p>

            <div className="settings-theme-grid">
              {THEME_OPTIONS.map(opt => {
                const Icon = opt.icon;
                const selected = theme === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    className={`settings-theme-option ${selected ? 'active' : ''}`}
                    onClick={() => setTheme(opt.id)}
                    aria-pressed={selected}
                  >
                    <ThemeSwatch variant={opt.id} />
                    <span className="settings-theme-option-label">
                      <Icon size={14} strokeWidth={2} />
                      {t(`appearance.theme.${opt.id}`, opt.label)}
                    </span>
                    {selected && <Check size={16} strokeWidth={2.5} className="settings-theme-option-check" />}
                  </button>
                );
              })}
            </div>

            {theme === 'auto' && (
              <p className="settings-hint mt-3">
                <Info size={14} strokeWidth={2} className="flex-shrink-0" />
                {t('appearance.autoHint', { theme: t(`appearance.theme.${effectiveTheme}`) })}
              </p>
            )}
          </div>
        );

      case 'language': {
        // Eigener Bereich (Task #76). Sprache liegt im common-Namespace, da app-weit; Persistenz
        // macht i18next selbst (localStorage 'sengi_lang'). Flaggen als Wiedererkennung, gleiche
        // Quelle wie der Topbar-Sprach-Link.
        const currentLang = i18n.resolvedLanguage ?? i18n.language;
        return (
          <div className="settings-section">
            <div className="settings-section-title">{t('common:language.label')}</div>
            <p className="settings-section-desc">{t('common:language.description')}</p>
            <div className="settings-theme-grid">
              {SUPPORTED_LANGUAGES.map(lng => {
                const selected = currentLang === lng;
                return (
                  <button
                    key={lng}
                    type="button"
                    className={`settings-theme-option ${selected ? 'active' : ''}`}
                    onClick={() => i18n.changeLanguage(lng)}
                    aria-pressed={selected}
                  >
                    <span className="settings-theme-option-label">
                      <FlagIcon lang={lng} height={16} />
                      {t(`common:language.${lng}`)}
                    </span>
                    {selected && <Check size={16} strokeWidth={2.5} className="settings-theme-option-check" />}
                  </button>
                );
              })}
            </div>
          </div>
        );
      }

      case 'notifications':
        return (
          <div className="settings-section">
            <div className="settings-section-title">{t('notifications.title')}</div>
            <p className="settings-section-desc">{t('notifications.description')}</p>

            <div className="settings-card p-0">
              {[
                { key: 'email' as const, icon: Mail },
                { key: 'desktop' as const, icon: MonitorSmartphone },
                { key: 'mobile' as const, icon: Smartphone },
                { key: 'marketing' as const, icon: Megaphone },
              ].map((row) => (
                <div className="settings-toggle-row" key={row.key}>
                  <row.icon size={18} strokeWidth={2} className="settings-toggle-icon" />
                  <div className="flex-grow-1">
                    <div className="settings-toggle-label">{t(`notifications.${row.key}`)}</div>
                    <div className="settings-toggle-desc">{t(`notifications.${row.key}Desc`)}</div>
                  </div>
                  <div className="form-check form-switch mb-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id={`notif-${row.key}`}
                      checked={notifications[row.key]}
                      onChange={() => toggleNotification(row.key)}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'account':
        return (
          <>
            <div className="settings-section">
              <div className="settings-section-title">{t('account.security')}</div>
              <p className="settings-section-desc">{t('account.securityDesc')}</p>
              <div className="settings-card d-flex flex-wrap gap-2">
                <button className="btn btn-outline-secondary d-flex align-items-center gap-2" disabled title={t('account.comingSoon')}>
                  <KeyRound size={15} strokeWidth={2} />
                  {t('account.changePassword')}
                </button>
                <button className="btn btn-outline-secondary d-flex align-items-center gap-2" disabled title={t('account.comingSoon')}>
                  <ShieldCheck size={15} strokeWidth={2} />
                  {t('account.twoFactor')}
                </button>
                <span className="settings-hint settings-hint-inline">{t('account.comingSoon')}</span>
              </div>
            </div>

            <div className="settings-section">
              <div className="settings-section-title">{t('account.localData')}</div>
              <p className="settings-section-desc">{t('account.localDataDesc')}</p>
              <div className="settings-card d-flex flex-wrap gap-2">
                <button className="btn btn-outline-secondary d-flex align-items-center gap-2" onClick={handleExportSettings}>
                  <Download size={15} strokeWidth={2} />
                  {t('account.exportSettings')}
                </button>
                <button className="btn btn-outline-danger d-flex align-items-center gap-2" onClick={handleClearLocalData}>
                  <Trash2 size={15} strokeWidth={2} />
                  {t('account.clearLocalData')}
                </button>
              </div>
            </div>

            <div className="settings-section">
              <div className="settings-section-title">{t('account.session')}</div>
              <p className="settings-section-desc">{t('account.sessionDesc', { name: displayName })}</p>
              <div className="settings-card">
                <button className="btn btn-outline-secondary d-flex align-items-center gap-2" onClick={handleLogout}>
                  <LogOut size={15} strokeWidth={2} />
                  {t('account.logout')}
                </button>
              </div>
            </div>

            <div className="settings-section settings-section-danger">
              <div className="settings-section-title text-danger">{t('account.dangerZone')}</div>
              <div className="settings-card settings-card-danger">
                <p className="small text-secondary mb-3">
                  {t('account.deleteHint')}
                </p>
                <div className="d-flex align-items-center gap-2">
                  <button className="btn btn-outline-danger btn-sm" disabled title={t('account.comingSoon')}>
                    {t('account.deleteAccount')}
                  </button>
                  <span className="settings-hint settings-hint-inline">{t('account.comingSoon')}</span>
                </div>
              </div>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className="container-fluid py-4 px-4 overflow-auto h-100 settings-page">
      <div className="settings-header">
        <h1 className="h3 mb-1">{t('header.title')}</h1>
        <p className="text-secondary mb-0">{t('header.subtitle')}</p>
      </div>

      <div className="settings-layout">
        <div className="settings-rail">
          <div className="settings-identity">
            <div className="settings-avatar">{getInitials(displayName)}</div>
            <div className="settings-identity-text">
              <div className="settings-identity-name">{displayName}</div>
              {showUserName && <div className="settings-identity-sub">{userName}</div>}
            </div>
          </div>

          <nav className="settings-nav">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const active = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  className={`settings-nav-item ${active ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                  aria-current={active}
                >
                  <Icon size={16} strokeWidth={2} />
                  <span>{t(`tabs.${tab.id}`, tab.name)}</span>
                </button>
              );
            })}
          </nav>
        </div>

        <div className="settings-panel">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
}
